import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../../../context/Auth";
import ExportData from "../../../components/common/ExportData";
import DebouncedSearchInput from "../../../components/common/DebouncedSearchInput";
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Package,
  Edit2,
  X,
  Calendar,
  Trash2,
} from "lucide-react";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import RoleBasedComponent from "../../../components/RoleBasedComponent";

const PrintBatchTracking = () => {
  const [searchParams] = useSearchParams();
  const branch = searchParams.get("branch");
  const drug = searchParams.get("drug");

  const { requestWithAuth, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [creditList, setCreditList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [totalPages, setTotalPages] = useState(0);
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [editData, setEditData] = useState({ quantity: "", expiry_date: "" });
  const [updating, setUpdating] = useState(false);
  const [errors, setErrors] = useState({});
  const [drugName, setDrugName] = useState("");
  const [quantity, setQuantity] = useState(null);
  const [branchName, setBranchName] = useState("");

  const isSuperAdmin = user?.role === "superadmin";

  const fetchBatchTracking = async (page_size, search_query, current_page) => {
    setLoading(true);
    try {
      const data = await requestWithAuth(
        `/inventory/branch/batches/list?page=${current_page}&pageSize=${page_size}&search=${search_query}&branch=${branch}&drug=${drug}`,
        "GET"
      );
      setDrugName(data?.drug_name);
      setQuantity(data?.quantity);
      setBranchName(data?.branch_name);
      setCreditList(data?.results);
      setNextPage(data?.next);
      setPrevPage(data?.prev);
      setTotalPages(Math.ceil(data?.count / page_size));
    } catch (error) {
      console.error("Error fetching list:", error);
    } finally {
      setLoading(false);
    }
  };

  const getTableData = async (page_size, search_query, current_page) => {
    try {
      const data = await requestWithAuth(
        `/inventory/branch/batches/list?paginated=false&search=${search_query}&branch=${branch}&drug=${drug}`,
        "GET"
      );
      let inventoryData = [];

      for (let i = 0; i <= data?.length - 1; i++) {
        inventoryData.push({
          ID: data[i]?.id,
          Drug: data[i]?.drug,
          Branch: data[i]?.branch,
          "Batch Number": data[i]?.batch_number,
          "Quantity Left": data[i]?.quantity,
          "Expiry Date": data[i]?.expiry_date,
        });
      }
      setTableData(inventoryData);
    } catch (error) {
      console.error("Error fetching list:", error);
    }
  };

  const filterCreditList = (search_query) => {
    setSearch(search_query);
    setCurrentPage(1);
  };

  const handleNextPage = () => {
    if (nextPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (prevPage) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const openEditModal = (batch) => {
    setSelectedBatch(batch);
    setEditData({
      quantity: batch.quantity,
      expiry_date: batch.expiry_date,
    });
    setErrors({});
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedBatch(null);
    setEditData({ quantity: "", expiry_date: "" });
    setErrors({});
  };

  const handleEditChange = (e) => {
    setEditData({ ...editData, [e.target.name]: e.target.value });
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: "" });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!editData.quantity) {
      newErrors.quantity = "Quantity is required";
    } else if (editData.quantity <= 0) {
      newErrors.quantity = "Quantity must be greater than 0";
    }
    if (!editData.expiry_date) {
      newErrors.expiry_date = "Expiry date is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUpdateBatch = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    setUpdating(true);
    try {
      await requestWithAuth(
        `/inventory/branch/batch/${selectedBatch.id}/`,
        "PUT",
        JSON.stringify(editData),
        {
          "Content-Type": "application/json",
        }
      );

      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Batch updated successfully",
      });

      closeModal();
      fetchBatchTracking(pageSize, search, currentPage);
      getTableData(pageSize, search, currentPage);
    } catch (error) {
      if (error.response?.data?.errors) {
        for (const property in error.response.data.errors) {
          toast.error(`${error.response.data.errors[property]}`);
        }
      } else {
        toast.error("Failed to update batch");
      }
      console.error("Error updating batch:", error);
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteBatch = async (batch) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!"
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await requestWithAuth(
            `/inventory/branch/batch/${batch.id}/`,
            "DELETE"
          );
          Swal.fire("Deleted!", "Batch has been deleted.", "success");
          fetchBatchTracking(pageSize, search, currentPage);
          getTableData(pageSize, search, currentPage);
        } catch (error) {
          toast.error("Failed to delete batch");
          console.error("Error deleting batch:", error);
        }
      }
    });
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const totalNumbersToShow = 3;

    let startPage = Math.max(1, currentPage - totalNumbersToShow);
    let endPage = Math.min(totalPages, currentPage + totalNumbersToShow);

    if (startPage > 1) {
      pageNumbers.push(1);
      if (startPage > 2) {
        pageNumbers.push("...");
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pageNumbers.push("...");
      }
      pageNumbers.push(totalPages);
    }

    return pageNumbers.map((number, idx) => (
      <button
        key={idx}
        onClick={() => number !== "..." && handlePageClick(number)}
        disabled={number === "..."}
        className={`min-w-[36px] h-[36px] flex items-center justify-center rounded-lg text-sm font-medium transition-all
          ${
            number === currentPage
              ? "bg-blue-500 text-white shadow-sm"
              : number === "..."
              ? "cursor-default text-gray-400"
              : "text-gray-600 hover:bg-gray-100"
          }`}
      >
        {number}
      </button>
    ));
  };

  useEffect(() => {
    fetchBatchTracking(pageSize, search, currentPage);
    getTableData(pageSize, search, currentPage);
  }, [search, currentPage, pageSize]);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                <span>Inventory</span>
                <span>/</span>
                <span className="text-gray-900 font-medium">
                  Batch Tracking
                </span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900">
                Batch Trackings
              </h1>
            </div>
            <RoleBasedComponent allowedRoles={["superadmin","stores"]}>
              <Link
                to={`/inventory/batch-tracking/new?branch=${branch}&drug=${drug}`}
                className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2.5 rounded-lg font-medium transition-colors shadow-sm flex items-center gap-2"
              >
                <Plus size={20} />
                Create New Batch
              </Link>
            </RoleBasedComponent>
          </div>
        </div>

        {/* Summary Cards */}
        {!loading && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            {/* Drug Name Card */}
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-sm p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm font-medium mb-1">
                    Drug Name
                  </p>
                  <h3 className="text-2xl font-bold">{drugName || "N/A"}</h3>
                </div>
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                  <Package size={24} />
                </div>
              </div>
            </div>

            {/* Total Quantity Card */}
            <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl shadow-sm p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-emerald-100 text-sm font-medium mb-1">
                    Total Quantity in Stock
                  </p>
                  <h3 className="text-2xl font-bold">{quantity}</h3>
                </div>
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                  <Package size={24} />
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl shadow-sm p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-orange-100 text-sm font-medium mb-1">
                    Branch Name
                  </p>
                  <h3 className="text-2xl font-bold">{branchName}</h3>
                </div>
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                  <Package size={24} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          {/* Filters Bar */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <ExportData tableData={tableData} fileName="Batch Tracking" />
                <span className="text-sm text-gray-600">Show</span>
                <select
                  onChange={(e) => setPageSize(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  value={pageSize}
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
                <span className="text-sm text-gray-600">entries</span>
              </div>

              <DebouncedSearchInput
                onSearch={filterCreditList}
                placeholder="Search batches..."
                delay={500}
                className="flex-1 max-w-md"
              />
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="flex items-center gap-3 text-gray-600">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                  <span>Loading batches...</span>
                </div>
              </div>
            ) : creditList?.length > 0 ? (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Branch
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Drug
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Batch Number
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Quantity Left
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Expiry Date
                    </th>
                    {isSuperAdmin && (
                      <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {creditList?.map((item, index) => (
                    <tr
                      key={index}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="py-4 px-6 text-sm text-gray-900">
                        {item?.branch}
                      </td>
                      <td className="py-4 px-6 text-sm font-medium text-gray-900">
                        {item?.drug}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {item?.batch_number}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {item?.quantity}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {item?.expiry_date}
                      </td>
                      <RoleBasedComponent
                        allowedRoles={["superadmin"]}
                        fallback={null}
                      >
                        <td className="py-4 px-6">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => openEditModal(item)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                            >
                              <Edit2 size={14} />
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteBatch(item)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                            >
                              <Trash2 size={14} />
                              Delete
                            </button>
                          </div>
                        </td>
                      </RoleBasedComponent>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="py-20 text-center">
                <div className="text-gray-400 mb-2">
                  <Package className="mx-auto h-12 w-12" strokeWidth={1.5} />
                </div>
                <p className="text-gray-500 text-sm">
                  No batch tracking data found
                </p>
                {search && (
                  <p className="text-gray-400 text-xs mt-1">
                    Try adjusting your search terms
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Pagination */}
          {creditList?.length > 0 && (
            <div className="p-6 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Showing page {currentPage} of {totalPages}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                    className={`p-2 rounded-lg transition-all ${
                      currentPage > 1
                        ? "text-gray-600 hover:bg-gray-100"
                        : "text-gray-300 cursor-not-allowed"
                    }`}
                  >
                    <ChevronLeft size={20} />
                  </button>

                  <div className="flex items-center gap-1">
                    {renderPageNumbers()}
                  </div>

                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className={`p-2 rounded-lg transition-all ${
                      currentPage < totalPages
                        ? "text-gray-600 hover:bg-gray-100"
                        : "text-gray-300 cursor-not-allowed"
                    }`}
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  Edit Batch
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Update batch quantity and expiry date
                </p>
              </div>
              <button
                onClick={closeModal}
                disabled={updating}
                className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
              >
                <X size={24} />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleUpdateBatch} className="p-6 space-y-4">
              {/* Batch Info */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Branch:</span>
                  <span className="font-medium text-gray-900">
                    {selectedBatch?.branch}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Drug:</span>
                  <span className="font-medium text-gray-900">
                    {selectedBatch?.drug}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Batch Number:</span>
                  <span className="font-medium text-gray-900">
                    {selectedBatch?.batch_number}
                  </span>
                </div>
              </div>

              {/* Quantity Field */}
              <div>
                <label
                  htmlFor="quantity"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Quantity <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="quantity"
                  id="quantity"
                  step="0.01"
                  value={editData.quantity}
                  onChange={handleEditChange}
                  className={`w-full border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 transition-all ${
                    errors.quantity
                      ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                      : "border-gray-300 focus:ring-blue-500 focus:border-transparent"
                  }`}
                  placeholder="Enter quantity"
                />
                {errors.quantity && (
                  <p className="mt-1.5 text-xs text-red-600">
                    {errors.quantity}
                  </p>
                )}
              </div>

              {/* Expiry Date Field */}
              <div>
                <label
                  htmlFor="expiry_date"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Expiry Date <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="date"
                    name="expiry_date"
                    id="expiry_date"
                    value={editData.expiry_date}
                    onChange={handleEditChange}
                    className={`w-full border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 transition-all ${
                      errors.expiry_date
                        ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                        : "border-gray-300 focus:ring-blue-500 focus:border-transparent"
                    }`}
                  />
                  <Calendar
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                    size={16}
                  />
                </div>
                {errors.expiry_date && (
                  <p className="mt-1.5 text-xs text-red-600">
                    {errors.expiry_date}
                  </p>
                )}
              </div>

              {/* Modal Footer */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={updating}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>

                {!updating ? (
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600 transition-colors shadow-sm"
                  >
                    Update Batch
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-400 rounded-lg cursor-not-allowed flex items-center gap-2"
                  >
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Updating...
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrintBatchTracking;
